import { makeStyles, Typography } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBackOutlined';
import React, { useContext, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { ClosureForm, ClosureValidateViolation } from 'types';
import { notify } from 'components/Notifier';
import Step1 from 'components/Forms/ClosureCloseForm/Step1';
import Step2 from 'components/Forms/ClosureCloseForm/Step2';
import Step3 from 'components/Forms/ClosureCloseForm/Step3';
import { AppContext } from 'context';
import { useClosureById } from 'hooks';
import NotFound from 'pages/NotFound/NotFound';
import { DialogWidth } from 'types';
import Step4 from 'components/Forms/ClosureCloseForm/Step4';
import StepInventoriedInboundStock from 'components/Forms/ClosureCloseForm/StepInventoriedInboundStock';
import StepSortingCenterContract from 'components/Forms/ClosureCloseForm/StepSortingCenterContract';
import MultiStepForm, { MultiStepFormStep } from 'components/Forms/MultiStepForm';
import { formatMonthForDisplay } from 'utils/date';
import Loading from 'components/Loading';
import ConfirmationPopInContent from 'components/Generic/ConfirmationPopInContent';
import NextIcon from 'components/Icons/NextIcon';
import Dialog from 'components/Dialog';
import { Routes } from '../../routes';

const useStyles = makeStyles({
  headerTitle: {
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'center',
  },
  paper: {
    flex: 1,
    position: 'relative',
    marginBottom: '6em',
  },
  paperHeader: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 12,
  },
  stepperHeader: {
    padding: '50px 50px 0 50px',
  },
  title: {
    margin: '0',
  },
  divider: {
    marginBottom: 12,
  },
  closeButton: {
    position: 'absolute',
    right: 20,
    top: 20,
  },
});

export type ClosureContext = {
  violations?: ClosureValidateViolation[];
  warnings?: ClosureValidateViolation[];
  stepInventoriedInboundStock?: {
    isSwitchInventaireOn?: boolean;
    inventaireParSeparationCheckboxes?: { [key: number]: boolean };
  };
};

const ClosureClose: React.FC = () => {
  const context = useContext(AppContext);
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const { t: pt } = useTranslation('translation', { keyPrefix: 'closureCloseForm' });

  const { id } = useParams<{ id: string }>();
  const [closure, loading] = useClosureById(id);
  const [step4ConfirmationPopInDisplay, setStep4ConfirmationPopInDisplay] = useState(false);

  if (loading) return <Loading />;

  if (!closure) {
    return <NotFound />;
  }

  const isAnnualClosure = 12 === closure.month;

  const onSubmit: SubmitHandler<ClosureForm> = async () => {
    const sortingCenterId = context.getSortingCenter?.()?.['@id'] ?? null;

    if (null === sortingCenterId) {
      notify({
        message: pt('creation.error'),
        severity: 'error',
      });
    }
  };

  const handlePopinExitBehaviour = (prepareNextMonth: boolean) => async () => {
    setStep4ConfirmationPopInDisplay(false);

    if (prepareNextMonth) {
      history.push(Routes.EstimatedReceptionsEdit);
    } else {
      history.push(Routes.Closures);
    }
  };

  /**
   * IMPORTANT POUR LES FORMS : Utiliser des identifiants uniques pour les étapes du formulaire
   * plutôt que des numéros permettra une meilleure flexibilité lors de la réorganisation des étapes,
   * sans avoir à mettre à jour les références dans le code.
   */
  const steps: MultiStepFormStep<ClosureForm, ClosureContext>[] = [
    { label: pt('step1.title'), component: Step1 },
    {
      label: pt('stepSortingCenterContract.title'),
      component: StepSortingCenterContract,
      nextButtonLabel: pt('stepSortingCenterContract.nextButtonLabel'),
    },
    {
      label: isAnnualClosure ? pt('step2.titleForAnnualClosure') : pt('step2.title'),
      component: Step2,
      nextButtonLabel: pt('step2.nextButtonLabel'),
    },
  ];

  if (isAnnualClosure) {
    steps.push({
      label: pt('stepInventoriedInboundStock.title'),
      component: StepInventoriedInboundStock,
      nextButtonLabel: pt('stepInventoriedInboundStock.nextButtonLabel'),
    });
  }

  steps.push({ label: pt('step3.title'), component: Step3, nextButtonLabel: pt('validate') });
  steps.push({
    label: pt('step4.title'),
    component: Step4,
    previousStepLabel: pt('terminate'),
    previousStepIcon: <></>,
    onClickPrevious: () => setStep4ConfirmationPopInDisplay(true),
    nextButtonLabel: pt('prepareNextMonth'),
    nextStepIcon: <NextIcon />,
    onClickNext: () => history.push(Routes.EstimatedReceptionsEdit),
  });

  const initialData = { closure, closureId: id, hasConfirmed: false };

  return (
    <>
      <div className={classes.headerTitle}>
        <Typography variant="h1" className={classes.title}>
          {pt('title', { month: formatMonthForDisplay(closure.month, closure.year), year: closure.year })}
        </Typography>
      </div>
      <Dialog
        closable={false}
        renderButton={() => {}}
        isOpen={step4ConfirmationPopInDisplay}
        onClose={() => setStep4ConfirmationPopInDisplay(false)}
        width={DialogWidth.SMALL_FORM}
        renderContent={() => (
          <ConfirmationPopInContent
            title={t('closures.prepareNextClosure.title', {
              month: formatMonthForDisplay(closure.month, closure.year),
              year: closure.year,
            })}
            cancelText={t('no')}
            validationText={t('yes')}
            cancelAction={handlePopinExitBehaviour(false)}
            validateAction={handlePopinExitBehaviour(true)}
          />
        )}
      />
      <MultiStepForm
        formName={'closureCloseForm'}
        backRoute="/closures"
        backIcon={<ArrowBackIcon />}
        onSubmit={onSubmit}
        steps={steps}
        initialData={initialData}
      />
    </>
  );
};
export default ClosureClose;
